import { Component, Input, NgZone, OnChanges, OnDestroy, ViewEncapsulation } from "@angular/core";
import { Dataset } from "chipster-js-common";
import * as d3 from "d3";
import { filter, map, find, difference, uniq, floor, intersection, range, flatMap, minBy } from "lodash-es";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { RestErrorService } from "../../../../../core/errorhandler/rest-error.service";
import { LoadState, State } from "../../../../../model/loadstate";
import TSVFile from "../../../../../model/tsv/TSVFile";
import { FileResource } from "../../../../../shared/resources/fileresource";
import UtilsService from "../../../../../shared/utilities/utils";
import { pointerPosition } from "../../../../../shared/visualization/plot.service";
import { VisualizationTSVService } from "../../../../../shared/visualization/visualizationTSV.service";
import { SessionDataService } from "../../session-data.service";
import Point from "../model/point";
import { SelectionRow } from "../model/selectionRow";
import { ExpressionProfileService } from "./expression-profile.service";
import GeneExpression from "./geneexpression";
import Interval from "./interval";
import Line from "./line";
import Rectangle from "./rectangle";

@Component({
  selector: "ch-expression-profile",
  templateUrl: "./expression-profile.component.html",
  styleUrls: ["./expression-profile.component.less"],
  encapsulation: ViewEncapsulation.None,
})
export class ExpressionProfileComponent implements OnChanges, OnDestroy {
  @Input()
  private dataset: Dataset;

  private tsv: TSVFile;
  selectedGeneExpressions: Array<GeneExpression>; // selected gene expressions
  // symbols and identifiers of the selected rows, shown in the selection list
  public viewSelectionList: Array<SelectionRow> = [];
  // files without a symbol column show the identifier column only
  public showSymbolColumn = false;
  // total number of rows, shown next to the number of selected rows
  public totalCount = 0;

  private unsubscribe: Subject<any> = new Subject();
  state: LoadState;

  // Redraws the chart when its container changes width, see observeResize()
  private resizeObserver: ResizeObserver;
  private lastChartWidth: number;

  constructor(
    private expressionProfileService: ExpressionProfileService,
    private sessionDataService: SessionDataService,
    private visualizationTSVService: VisualizationTSVService,
    private fileResource: FileResource,
    private restErrorService: RestErrorService,
    private zone: NgZone,
  ) {}

  ngOnChanges() {
    // unsubscribe from previous subscriptions
    this.unsubscribe.next(null);
    this.state = new LoadState(State.Loading, "Loading data...");
    setTimeout(() => this.update(), 100);
  }

  update() {
    const datasetName = this.dataset.name;

    // check for empty file
    if (this.dataset.size < 1) {
      this.state = new LoadState(State.EmptyFile);
      return;
    }
    this.fileResource
      .getData(this.sessionDataService.getSessionId(), this.dataset)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: (result: any) => {
          const parsedTSV = d3.tsvParseRows(result);
          this.tsv = new TSVFile(parsedTSV, this.dataset.datasetId, datasetName);
          this.totalCount = this.tsv.body.size();
          if (this.visualizationTSVService.containsChipHeaders(this.tsv)) {
            this.drawLineChart(this.tsv);
            this.observeResize();
            this.state = new LoadState(State.Ready);
          } else {
            this.state = new LoadState(
              State.Fail,
              "Only microarray data supported, no columns starting with chip found.",
            );
          }
        },
        error: (error: any) => {
          this.state = new LoadState(State.Fail, "Loading data failed");
          this.restErrorService.showError(this.state.message, error);
        },
      });

    this.selectedGeneExpressions = [];
    this.viewSelectionList = [];

    // reset the cached width so the resize observer redraws the new dataset
    // even when the container returns to a width it had for a previous one
    this.lastChartWidth = undefined;
  }

  ngOnDestroy() {
    this.unsubscribe.next(null);
    this.unsubscribe.complete();
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = undefined;
    }
  }

  /**
   * Redraw the chart whenever its container changes size.
   *
   * The chart is drawn into a tab that ngbNav creates lazily. When the tab
   * becomes visible the container doesn't necessarily have its final width
   * yet, so the first draw can end up with a zero/wrong width and an empty
   * chart. Observing the container redraws the chart once it gets its real
   * size, and also takes care of redrawing on window resize.
   *
   * Safe to call repeatedly; the observer is only created once.
   */
  private observeResize() {
    const element = document.getElementById("expressionprofile");
    if (!element || this.resizeObserver) {
      return;
    }
    this.resizeObserver = new ResizeObserver((entries) => {
      // Only width changes trigger a redraw: the chart height is fixed, so
      // height-only changes (e.g. the selection list growing the panel) must
      // not redraw, otherwise the current selection highlight would be lost.
      const width = entries[0]?.contentRect.width;
      if (width && width !== this.lastChartWidth && this.tsv) {
        this.lastChartWidth = width;
        // ResizeObserver fires outside Angular's zone; redraw inside it so the
        // d3 drag handlers are re-registered in the zone (otherwise selecting
        // would not trigger change detection) and bound state stays in sync.
        this.zone.run(() => this.drawLineChart(this.tsv));
      }
    });
    this.resizeObserver.observe(element);
  }

  drawLineChart(tsv: TSVFile) {
    // Configurate svg and graph-area
    const expressionprofileWidth = document.getElementById("expressionprofile").offsetWidth;
    const margin = { top: 10, right: 0, bottom: 150, left: 40 };
    const size = { width: expressionprofileWidth, height: 600 };
    const graphArea = {
      width: size.width,
      height: size.height - margin.top - margin.bottom,
    };

    // SVG-element
    // A click selects a single line, so allow a little movement before d3 counts
    // the gesture as a drag and swallows the click. Its default is no movement
    // at all. The same threshold decides whether the finished gesture was a click
    // or a selection rectangle, like in the scatter and volcano plots.
    const clickDistance = 4;
    const drag = d3.drag().clickDistance(clickDistance);

    // where the press of the gesture landed, and whether the drag behaviour took
    // the gesture, see the click handler further down
    let pressPoint: Point = null;
    let gestureStarted = false;

    const profile = d3.select("#expressionprofile");

    profile.select("svg").remove();

    const svg = profile
      .append("svg")
      .attr("width", size.width)
      .attr("height", size.height)
      .attr("id", "svg")
      // keep a drag from extending a text selection elsewhere on the page, which
      // scrolls the page instead of drawing the selection rectangle. Must come
      // before the drag, which stops immediate propagation of mousedown.
      .on("mousedown", (event) => {
        event.preventDefault();
        pressPoint = pointerPosition(event, document.getElementById("dragGroup"));
        gestureStarted = false;
      })
      .call(drag);

    // Custom headers for x-axis
    const firstDataset: any = this.dataset;
    const phenodataDescriptions = filter(firstDataset.metadata, (metadata: any) => metadata.key === "description");

    // Change default headers to values defined in phenodata if description value has been defined
    const headers = map(this.visualizationTSVService.getChipHeaders(tsv), (header) => {
      // find if there is a phenodata description matching header and containing a value
      const phenodataHeader: any = find(
        phenodataDescriptions,
        (item: any) => item.column === header && item.value !== null,
      );
      return phenodataHeader ? phenodataHeader.value : header;
    });

    // X-axis and scale
    // Calculate points (in pixels) for positioning x-axis points
    const chipRange = map(headers, (item, index) => (graphArea.width / headers.length) * index);
    const xScale = d3.scaleOrdinal().range(chipRange).domain(headers);
    // compile error hidden with <any>: Argument of type 'ScaleOrdinal<string, {}>' is not
    // assignable to parameter of type 'AxisScale<string>'.
    const xAxis = d3.axisBottom(<any>xScale).ticks(headers.length);
    svg
      .append("g")
      .attr("class", "x axis")
      .attr("transform", "translate(" + margin.left + "," + graphArea.height + ")")
      .call(xAxis)
      .selectAll("text")
      .attr("transform", "rotate(-65 0 0)")
      .style("text-anchor", "end");

    // Linear x-axis to determine selection-rectangle position scaled to tsv-data
    const linearXScale = d3
      .scaleLinear()
      .range([0, graphArea.width - graphArea.width / headers.length])
      .domain([0, headers.length - 1]);

    // Y-axis and scale
    const yScale = d3
      .scaleLinear()
      // the top of the range is margin.top, not 0, so that a line at the maximum
      // value isn't drawn against the top edge of the svg, which clips it
      .range([graphArea.height, margin.top])
      .domain([
        this.visualizationTSVService.getDomainBoundaries(tsv).min,
        this.visualizationTSVService.getDomainBoundaries(tsv).max,
      ]);
    const yAxis = d3.axisLeft(yScale).ticks(5);
    svg
      .append("g")
      .attr("class", "y axis")
      .attr("transform", "translate(" + margin.left + ",0 )")
      .call(yAxis);

    // Paths
    const pathsGroup = svg
      .append("g")
      .attr("id", "pathsGroup")
      .attr("transform", "translate(" + margin.left + ",0)");
    const lineGenerator = d3
      .line()
      .x((d: [number, number], i: number) => parseFloat(xScale(headers[i]).toString()))
      .y((d: any) => yScale(d));

    const color = d3.scaleOrdinal(d3.schemeCategory10);

    const geneExpressions = this.visualizationTSVService.getGeneExpressions(tsv);
    const orderedExpressionGenes = this.visualizationTSVService.orderBodyByFirstValue(geneExpressions);

    pathsGroup
      .selectAll(".path")
      .data(orderedExpressionGenes)
      .enter()
      .append("path")
      .attr("class", "path")
      .attr("id", (d: GeneExpression) => "path" + d.id)
      // compile error hidden with <any>: Argument of type 'number[]' is not assignable to parameter of type '[number, number][]'.
      .attr("d", (d: GeneExpression) => lineGenerator(<any>d.values))
      .attr("fill", "none")
      .attr("stroke-width", 1)
      .attr("stroke", (d: any, i: number) => {
        //     There are 20 different colors in colorcategory. Setting same color for each consecutive 5% of lines.
        //     So for 100 lines 5 first lines gets first color in category, next 5 lines get second color and so on.
        const colorIndex = floor((i / tsv.body.size()) * 20).toString();
        return color(colorIndex);
      })
      .on("mouseover", (event, d: any) => {
        this.setSelectionHoverStyle(d.id);
      })
      .on("mouseout", (event, d: any) => {
        this.removeSelectionHoverStyle(d.id);
      });

    // path animation
    // paths.each(function(d: any) { d.totalLength = this.getTotalLength(); })
    //     .attr("stroke-dasharray", function(d:any) { return d.totalLength + " " + d.totalLength; })
    //     .attr("stroke-dashoffset", function(d:any) { return d.totalLength; })
    //     .transition()
    //     .duration(2000)
    //     .ease('linear')
    //     .attr('stroke-dashoffset', 0);

    // Dragging
    const dragGroup = svg
      .append("g")
      .attr("id", "dragGroup")
      .attr("transform", "translate(" + margin.left + ",0)");

    // Create selection rectangle
    dragGroup
      .append("rect")
      .attr("width", 0)
      .attr("height", 0)
      .attr("x", 0)
      .attr("y", 0)
      .attr("class", "band")
      .attr("id", "band");

    let startPoint = new Point(-1, -1); // startpoint for dragging

    // Register drag handlers
    drag.on("start", (event) => {
      gestureStarted = true;

      startPoint = pointerPosition(event, document.getElementById("dragGroup"));
    });

    drag.on("drag", (event) => {
      const endPoint = pointerPosition(event, document.getElementById("dragGroup"));

      // the rectangle is drawn from its upper left corner, whichever corner the
      // gesture started from
      const minX = Math.min(startPoint.x, endPoint.x);
      const minY = Math.min(startPoint.y, endPoint.y);

      d3.select(".band")
        .attr("transform", "translate(" + minX + "," + minY + ")")
        .transition()
        .duration(1)
        .attr("width", Math.abs(startPoint.x - endPoint.x))
        .attr("height", Math.abs(startPoint.y - endPoint.y));
    });

    const resetSelectionRectangle = (): void => {
      startPoint = new Point(-1, -1);
      d3.select(".band").attr("width", 0).attr("height", 0).attr("x", 0).attr("y", 0);
    };

    // The lines are one pixel wide, so a click that narrowly misses one should
    // still select it. Only the intervals the box crosses need to be checked.
    const closestLineId = (point: Point): string => {
      const tolerance = 5;
      const boxStart = new Point(point.x - tolerance, point.y - tolerance);
      const boxEnd = new Point(point.x + tolerance, point.y + tolerance);
      const intervalIndexes = this.expressionProfileService.getCrossingIntervals(boxStart, boxEnd, linearXScale, tsv);

      /*
       * getCrossingIntervals() clamps the start up to zero but leaves the end as
       * it is, so a click outside the plot can give an end smaller than the
       * start. range() counts down from such a pair, so clamp it: the click
       * crosses no interval at all and there is nothing to measure.
       */
      const end = Math.max(intervalIndexes.start, intervalIndexes.end);
      const distanceTo = (line: Line) => this.expressionProfileService.distanceToLine(point, line);
      const lines = flatMap(range(intervalIndexes.start, end), (chipValueIndex: number) =>
        this.expressionProfileService.createLines(tsv, chipValueIndex, linearXScale, yScale),
      );
      const closest = minBy(lines, distanceTo);

      return closest != null && distanceTo(closest) < tolerance ? closest.lineId : null;
    };

    // The drag behaviour ignores a gesture that is made with ctrl held, so such a
    // click never reaches the drag handler below, which is the only place that
    // selects a line. Handle the clicks it declined here instead, see the same
    // handler and the reasons for it in plot.directive.ts.
    svg.on("click", (event) => {
      if (gestureStarted || pressPoint == null) {
        return;
      }
      const releasePoint = pointerPosition(event, document.getElementById("dragGroup"));
      const dx = releasePoint.x - pressPoint.x;
      const dy = releasePoint.y - pressPoint.y;
      if (dx * dx + dy * dy > clickDistance * clickDistance) {
        return;
      }
      const nearbyId = closestLineId(pressPoint) ?? closestLineId(releasePoint);
      if (nearbyId != null) {
        this.selectLine(event, nearbyId);
      }
    });

    drag.on("end", (event) => {
      const endPoint = pointerPosition(event, document.getElementById("dragGroup"));

      const sourceEvent = event.sourceEvent ?? event;
      const isShift = UtilsService.isShiftKey(sourceEvent);
      const isCtrl = UtilsService.isCtrlKey(sourceEvent);

      // d3 reports a drag for every mousemove, so the gesture is a click until it
      // has moved further than the click threshold. Comparing the coordinates
      // themselves would make a one pixel drift a rectangle that selects nothing,
      // and a truly horizontal or vertical drag a click.
      const dx = endPoint.x - startPoint.x;
      const dy = endPoint.y - startPoint.y;

      if (dx * dx + dy * dy > clickDistance * clickDistance) {
        // Shift adds the lines in the rectangle to the current selection and cmd
        // toggles them, otherwise the rectangle replaces the selection. Only cmd,
        // because d3 doesn't start a drag gesture when ctrl is held.
        if (!isShift && !isCtrl) {
          this.resetSelections();
        }
        d3.selectAll(".path").attr("stroke-width", 1);

        const intervalIndexes = this.expressionProfileService.getCrossingIntervals(
          endPoint,
          startPoint,
          linearXScale,
          tsv,
        );
        const intervals: Array<Interval> = [];

        // create intervals
        for (let chipValueIndex = intervalIndexes.start; chipValueIndex < intervalIndexes.end; chipValueIndex++) {
          const lines = this.expressionProfileService.createLines(tsv, chipValueIndex, linearXScale, yScale);
          const intervalStartIndex = chipValueIndex;

          const rectangle = new Rectangle(endPoint.x, endPoint.y, startPoint.x, startPoint.y);
          intervals.push(new Interval(intervalStartIndex, lines, rectangle));
        }

        let ids: Array<string> = []; // path ids found in each interval (not unique list)
        for (const interval of intervals) {
          const intersectingLines = filter(interval.lines, (line: Line) =>
            this.expressionProfileService.isIntersecting(line, interval.rectangle),
          );

          // Line ids intersecting with selection as an array
          ids = ids.concat(map(intersectingLines, (line: Line) => line.lineId));
        }

        if (isCtrl) {
          this.toggleSelections(uniq(ids));
        } else {
          this.addSelections(uniq(ids));
        }

        // remove duplicate ids
        resetSelectionRectangle();
      } else {
        // A click, not a rectangle selection. The drag is registered on the whole
        // svg, so this branch handles every click, whether it landed on a line or
        // not. The lines cannot handle their own, because the browser dispatches
        // the click on the closest common ancestor of the elements the press and
        // the release landed on, which is the svg as soon as the mouse moved off a
        // line, even by less than the click distance. A click that misses selects
        // the closest line, and only a click with no line near it clears the
        // selection, like in the scatter and volcano plots.
        //
        // Shift and cmd clicks add to or toggle the selection, so they must not
        // clear it. Ctrl never gets here, because d3 doesn't start a drag gesture
        // when ctrl is held; the click handler above takes care of a ctrl click.
        //
        // Look around both ends of the gesture, preferring the press: a click
        // that starts on a line means that line, however the mouse drifted before
        // the release, and the drift can take the release further away than the
        // tolerance.
        //
        // The release is worth a search of its own only when it is somewhere
        // else. A click of a mouse that didn't move at all is the common case,
        // and both ends of it would give the same answer.
        const moved = dx !== 0 || dy !== 0;
        const nearbyId = closestLineId(startPoint) ?? (moved ? closestLineId(endPoint) : null);
        if (nearbyId != null) {
          this.selectLine(sourceEvent, nearbyId);
        } else if (!isShift && !isCtrl) {
          this.resetSelections();
        }
        resetSelectionRectangle();
      }
    });

    // the paths are recreated on every redraw, so re-apply the selection highlight
    (this.selectedGeneExpressions ?? []).forEach((expression) => this.setSelectionStyle(expression.id));
  }

  createNewDataset() {
    const selectedGeneExpressionIds = this.getSelectionIds();
    const tsvData = this.tsv.getRawDataByRowIds(selectedGeneExpressionIds);
    const data = d3.tsvFormatRows(tsvData);
    this.sessionDataService
      .createDerivedDataset("dataset.tsv", [this.dataset], "Expression profile", data)
      .subscribe(null);
  }

  getSelectionIds(): Array<string> {
    return this.selectedGeneExpressions.map((expression: GeneExpression) => expression.id);
  }

  /**
   * @description select one line, e.g. when it is clicked
   *
   * Shift adds to the selection and ctrl or cmd toggles.
   */
  selectLine(event, id: string): void {
    if (UtilsService.isShiftKey(event)) {
      this.addSelections([id]);
    } else if (UtilsService.isCtrlKey(event)) {
      this.toggleSelections([id]);
    } else {
      this.resetSelections();
      this.addSelections([id]);
    }
  }

  resetSelections(): void {
    this.removeSelections(this.getSelectionIds());
    this.selectedGeneExpressions.length = 0;
    this.setViewSelectionList();
  }

  removeSelections(ids: Array<string>): void {
    for (const id of ids) {
      this.removeSelectionStyle(id);
    }

    /*
     * The gene expressions of the remaining selection are in this array already,
     * so drop the removed ones instead of looking them all up again.
     */
    const removedIds = new Set(ids);
    this.selectedGeneExpressions = this.selectedGeneExpressions.filter(
      (geneExpression: GeneExpression) => !removedIds.has(geneExpression.id),
    );
    this.setViewSelectionList();
  }

  addSelections(ids: Array<string>) {
    const selectionIds = this.getSelectionIds();
    const missingSelectionIds = difference(ids, selectionIds);
    // look up the rows of the whole selection at once, a row at a time takes seconds over a large file
    const missingGeneExpressions = this.visualizationTSVService.getGeneExpressionsByIds(this.tsv, missingSelectionIds);
    this.selectedGeneExpressions = this.selectedGeneExpressions.concat(missingGeneExpressions);
    missingSelectionIds.forEach((id) => {
      this.setSelectionStyle(id);
    });
    this.setViewSelectionList();
  }

  toggleSelections(ids: Array<string>) {
    const selectionIds = this.getSelectionIds();
    const selectionIdsToAdd = difference(ids, selectionIds);
    const selectionIdsToRemove = intersection(ids, selectionIds);
    this.addSelections(selectionIdsToAdd);
    this.removeSelections(selectionIdsToRemove);
  }

  setSelectionStyle(id: string) {
    d3.select("#path" + id).classed("selected", true);
  }

  removeSelectionStyle(id: string) {
    d3.select("#path" + id).classed("selected", false);
  }

  setSelectionHoverStyle(id: string) {
    d3.select("#path" + id).classed("pathover", true);
  }

  removeSelectionHoverStyle(id: string) {
    d3.select("#path" + id).classed("pathover", false);
  }

  setViewSelectionList(): void {
    const rowIds = this.selectedGeneExpressions.map((geneExpression: GeneExpression) => geneExpression.id);
    this.showSymbolColumn = this.visualizationTSVService.containsSymbolColumn(this.tsv);
    this.viewSelectionList = this.visualizationTSVService.getSelectionRows(this.tsv, rowIds);
  }
}
